import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import {
    CommissioningStore,
    FEATURES_PANEL_TYPES,
    GatewayDevice,
    InstallationFeaturesStore,
    RemoteDeviceCode,
    RemoteDeviceType,
    ZIGBEE_COORDINATOR_STATUS,
} from '@store';

import {
    DeviceIconName,
    DeviceIds,
    DeviceRoles,
    Feature,
    FeatureCommands,
    FeatureNames,
    FeaturePropertyKey,
    OtherRoomCommands,
    OtherRoomProperties,
    RoomCommands,
    RoomProperties,
} from '@vi-iot/client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    CommissioningAddRoomModalStandaloneComponent,
    CommissioningEditRoomModalComponent,
    CommissioningProcessModalComponent,
} from './';
import { DEVICES_TO_COMMISSIONING, Device, ModalAction } from './commissioning.model';
import { CommissioningStrategy, initialStrategy } from './commissioning.strategy';

@Injectable({
  providedIn: 'root',
})
export class CommissioningService {
  activeStrategy$ = new BehaviorSubject<CommissioningStrategy>(initialStrategy());
  modalAction$ = new Subject<ModalAction>();
  pairingStatusName$ = new BehaviorSubject<ZIGBEE_COORDINATOR_STATUS>(
    ZIGBEE_COORDINATOR_STATUS.INITIATING
  );
  currentRemoteDeviceCode$ = new BehaviorSubject<RemoteDeviceCode>({
    id: null,
    ic: null,
  });

  addRoomDialogRef: MatDialogRef<CommissioningAddRoomModalStandaloneComponent>;
  editRoomDialogRef: MatDialogRef<CommissioningEditRoomModalComponent>;
  addDeviceDialogRef: MatDialogRef<CommissioningProcessModalComponent>;

  readonly panelTypes = FEATURES_PANEL_TYPES;

  devicesToCommissioning$: BehaviorSubject<Device[]> = new BehaviorSubject<Device[]>(
    DEVICES_TO_COMMISSIONING
  );
  selectedDevice$: BehaviorSubject<Device> = new BehaviorSubject<Device>(
    DEVICES_TO_COMMISSIONING[0]
  );
  isVitotrol300E$: Observable<boolean>;

  constructor(
    private commissioningStore: CommissioningStore,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  updateStrategy(newStrategy: CommissioningStrategy): void {
    this.activeStrategy$.next(newStrategy);
  }

  openAddRoomDialog(): void {
    this.addRoomDialogRef = this.dialog.open(CommissioningAddRoomModalStandaloneComponent, {
      width: '710px',
      panelClass: 'commissioning-add-room-dialog',
      data: {},
      disableClose: true,
    });
  }

  openEditRoomDialog(
    room: Feature<RoomProperties, RoomCommands> | Feature<OtherRoomProperties, OtherRoomCommands>,
    isOtherRoom?: boolean
  ): void {
    this.editRoomDialogRef = this.dialog.open(CommissioningEditRoomModalComponent, {
      width: '715px',
      panelClass: 'commissioning-edit-room-dialog',
      data: {
        room,
        isOtherRoom,
      },
    });
  }

  deleteRoom(room: Feature<RoomProperties, RoomCommands>): void {
    this.installationFeaturesStore.updateFeature(
      room.feature,
      null,
      null,
      null,
      FeatureCommands.remove,
      null,
      DeviceIds.RoomControl,
      {
        errorMessage: this.translateService.instant('+COMMISSIONING.TOAST.ERROR.REMOVE_ROOM'),
        successMessage: this.translateService.instant('+COMMISSIONING.TOAST.SUCCESS.REMOVE_ROOM'),
      }
    );
  }

  openAddDeviceDialog(room?: string): void {
    this.addDeviceDialogRef = this.dialog.open(CommissioningProcessModalComponent, {
      width: '710px',
      panelClass: 'commissioning-add-device-dialog',
      data: {
        room,
      },
      disableClose: true,
      autoFocus: false,
    });

    this.addDeviceDialogRef.afterClosed().subscribe(() => {
      this.devicesToCommissioning$.next(DEVICES_TO_COMMISSIONING);
      this.selectedDevice$.next(DEVICES_TO_COMMISSIONING[0]);
    });

    this.isVitotrol300E$ = this.selectedDevice$.pipe(
      map((device) => device.icon.includes(DeviceIconName.vitotrol300E))
    );
  }

  deleteDevice(device: GatewayDevice): void {
    const id = device.id.replace('zigbee-', '');
    this.commissioningStore.updateFeatureProperty(
      FeatureNames.deviceZigbeeCoordinator,
      id,
      null,
      'id',
      FeatureCommands.removeDevice,
      {
        actionName: FeatureCommands.removeDevice,
        errorMessage: this.translateService.instant('+COMMISSIONING.TOAST.ERROR.REMOVE_DEVICE'),
        successMessage: this.translateService.instant('+COMMISSIONING.TOAST.SUCCESS.REMOVE_DEVICE'),
      }
    );
  }

  unassignDeviceFromRoom(deviceId: string, room: Feature<RoomProperties, RoomCommands>): void {
    this.installationFeaturesStore.updateFeature(
      room.feature,
      deviceId,
      null,
      'actorDeviceId',
      FeatureCommands.removeActor,
      null,
      DeviceIds.RoomControl,
      {
        errorMessage: this.translateService.instant(
          '+COMMISSIONING.TOAST.ERROR.UNASSIGN_DEVICE_FROM_ROOM'
        ),
        successMessage: this.translateService.instant(
          '+COMMISSIONING.TOAST.SUCCESS.UNASSIGN_DEVICE_FROM_ROOM'
        ),
      }
    );
  }

  setChannelRoomInfluence(channelFeature: string, active: boolean): void {
    this.installationFeaturesStore.updateFeature(
      channelFeature,
      active,
      null,
      FeaturePropertyKey.active,
      FeatureCommands.setRoomInfluenceEnabled,
      null,
      DeviceIds.RoomControl
    );
  }

  deactivateSRC(): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomStatus,
      false,
      FeaturePropertyKey.active,
      '',
      FeatureCommands.deactivate,
      null,
      DeviceIds.RoomControl,
      {
        successMessage: this.translateService.instant('+INSTALLATIONS.SRC.TOAST.SUCCESS.SRC_OFF'),
      }
    );
  }

  // Used in standalone modals
  assignDeviceToRoomStandalone(
    deviceId: string,
    roomFeature: string,
    heatingCircuitId?: number,
    isSelectedDeviceOBNG = false
  ): void {
    const toast = {
      errorMessage: this.translateService.instant(
        '+COMMISSIONING.TOAST.ERROR.ASSIGN_DEVICE_TO_ROOM'
      ),
      successMessage: this.translateService.instant(
        '+COMMISSIONING.TOAST.SUCCESS.ASSIGN_DEVICE_TO_ROOM'
      ),
    };

    if (isSelectedDeviceOBNG) {
      if (heatingCircuitId != null) {
        // OBNG: Vitotrol/eTRV are assigned to a room together with a channel (heating circuit).
        this.installationFeaturesStore.updateFeature(
          roomFeature,
          [deviceId, String(heatingCircuitId)],
          null,
          [FeaturePropertyKey.actorDeviceId, FeaturePropertyKey.channel],
          FeatureCommands.addActor,
          null,
          DeviceIds.RoomControl,
          toast
        );

        return;
      }

      // OBNG: assign the device to the room by device id only, no channel.
      this.installationFeaturesStore.updateFeature(
        roomFeature,
        deviceId,
        null,
        FeaturePropertyKey.actorDeviceId,
        FeatureCommands.addActor,
        null,
        DeviceIds.RoomControl,
        toast
      );

      return;
    }

    this.installationFeaturesStore.updateFeature(
      roomFeature,
      [deviceId, heatingCircuitId],
      null,
      [FeaturePropertyKey.actorDeviceId, FeaturePropertyKey.heatingCircuit],
      FeatureCommands.addActor,
      null,
      DeviceIds.RoomControl,
      toast
    );
  }

  getDevicesCount(groupDevices: { type: string; devices: GatewayDevice[] }[]): number {
    if (groupDevices) {
      return groupDevices
        .filter(this.filterDevicesBeforeCount)
        .map((g) => g.devices.length)
        .reduce((len1, len2) => len1 + len2);
    }
  }

  private filterDevicesBeforeCount = (device): boolean => {
    //Filter devices not vitotrol type to not affect to the number of devices in src
    return device.type !== RemoteDeviceType.VITOTROL_100E;
  };

  invalidVitotrolToAssignRoom(device: GatewayDevice): boolean {
    return !device?.roles.includes(DeviceRoles.rcSensor);
  }

  isVitotrol300EDevice(device: GatewayDevice): boolean {
    return device?.roles.includes(DeviceRoles.RemoteControllerVitotrol300E);
  }
}
